import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Label } from '../components/ui/label'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Spinner } from '../components/ui/spinner'
import { Alert } from '../components/ui/alert'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog'
import { ArrowLeft, Calendar, Tag, Users, Edit, Save, Upload, X, Trash2 } from 'lucide-react'

interface GlossaryLabelPair {
  value: string
  label: string
}

interface CampaignDetails {
  id: number
  title: { rendered: string }
  status: string
  permalink: string
  campaign_type: GlossaryLabelPair[]
  country: GlossaryLabelPair[]
  category: GlossaryLabelPair[]
  social_media_platforms: GlossaryLabelPair[]
  start_date: string
  end_date: string
  created_date: string
  featured_image: string
  description: string
  company: {
    id: number
    name: string
  }
}

// Helper functions for campaign display
const getSocialMediaIcon = (platform: string) => {
  const config = (window as any).WP_COCKPIT
  // Use theme URL instead of plugin assets URL
  const themeUrl = config?.themeUrl || 'https://influencer.kainjoo.com/wp-content/themes/kj-influencer'
  const iconUrl = `${themeUrl}/assets/images/glossary-icons/${platform}.svg`
  
  return (
    <img 
      src={iconUrl} 
      alt={`${platform} icon`}
      className="w-full h-full object-contain"
      onError={(e) => {
        // Fallback to generic icon if SVG not found
        e.currentTarget.style.display = 'none'
        const fallback = document.createElement('div')
        fallback.className = 'w-full h-full bg-gray-400 rounded-sm flex items-center justify-center text-xs font-bold text-white'
        fallback.textContent = platform.charAt(0).toUpperCase()
        e.currentTarget.parentElement!.appendChild(fallback)
      }}
    />
  )
}

const getCountryFlag = (countryValue: string) => {
  const config = (window as any).WP_COCKPIT
  // Use theme URL instead of plugin assets URL
  const themeUrl = config?.themeUrl || 'https://influencer.kainjoo.com/wp-content/themes/kj-influencer'
  const flagUrl = `${themeUrl}/assets/images/glossary-icons/${countryValue}.svg`
  
  return (
    <img 
      src={flagUrl} 
      alt={`${countryValue} flag`}
      className="w-4 h-4 rounded-sm"
      onError={(e) => {
        // Fallback to generic icon if flag not found
        e.currentTarget.style.display = 'none'
        const fallback = document.createElement('div')
        fallback.className = 'w-4 h-4 bg-gray-200 rounded-sm flex items-center justify-center text-xs'
        fallback.textContent = '🌍'
        e.currentTarget.parentElement!.appendChild(fallback)
      }}
    />
  )
}

const getCampaignTypeIcon = (campaignType: string) => {
  const config = (window as any).WP_COCKPIT
  // Use theme URL for campaign type icons - same path as other glossary icons
  const themeUrl = config?.themeUrl || 'https://influencer.kainjoo.com/wp-content/themes/kj-influencer'
  const iconUrl = `${themeUrl}/assets/images/glossary-icons/${campaignType}.png`
  
  return (
    <img 
      src={iconUrl} 
      alt={`${campaignType} icon`}
      className="w-4 h-4 object-contain"
      onError={(e) => {
        // Fallback to generic icon if PNG not found
        e.currentTarget.style.display = 'none'
        const fallback = document.createElement('div')
        fallback.className = 'w-4 h-4 bg-blue-100 rounded-sm flex items-center justify-center text-xs font-bold text-blue-600'
        fallback.textContent = campaignType.charAt(0).toUpperCase()
        e.currentTarget.parentElement!.appendChild(fallback)
      }}
    />
  )
}

const getStatusBadgeVariant = (status: string) => {
  const normalizedStatus = status.toLowerCase()
  switch (normalizedStatus) {
    case 'publish':
      return 'default' // Green for published
    case 'draft':
      return 'secondary' // Gray for draft
    case 'pending':
      return 'outline' // Orange for pending
    case 'private':
      return 'destructive' // Red for private
    default:
      return 'outline'
  }
}

const capitalizeFirst = (str: string) => {
  if (str === 'publish') return 'Published'
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const formatGlossaryLabels = (items: GlossaryLabelPair[]) => {
  if (!items || items.length === 0) return 'Not specified'
  return items.map(item => item.label).join(', ')
}

const formatDate = (dateString: string): string => {
  if (!dateString) return 'Not specified'
  
  if (dateString.includes('/')) {
    // Handle MM/DD/YYYY format
    return dateString
  } else {
    // Handle ISO date format
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid date'
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch (error) {
      console.error('Error formatting date:', error)
      return dateString
    }
  }
}

export default function CampaignDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [campaign, setCampaign] = useState<CampaignDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Glossary options for selectors
  const [glossaryOptions, setGlossaryOptions] = useState({
    country: [] as GlossaryLabelPair[],
    category: [] as GlossaryLabelPair[],
    social_media_platforms: [] as GlossaryLabelPair[],
    campaign_type: [] as GlossaryLabelPair[]
  })

  // Form data for editing
  const [formData, setFormData] = useState({
    title: '',
    status: 'publish',
    company_name: '',
    campaign_type: [] as string[],
    country: [] as string[],
    category: [] as string[],
    social_media_platforms: [] as string[],
    start_date: '',
    end_date: '',
    featured_image: '',
    description: ''
  })

  // Load glossary options for selectors
  const loadGlossaryOptions = async () => {
    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        console.error('WordPress config not available:', config)
        return
      }

      const isDev = config?.devMode || false
      if (isDev) console.log('Loading glossary options...')

      const [countryRes, categoryRes, smPlatformsRes, campaignTypeRes] = await Promise.all([
        // REST route expects the glossary ID as a path param: /jetengine-glossary/{id}
        fetch(`${config.restApiUrl}kj-cockpit/v1/jetengine-glossary/1`, {
          headers: { 'X-WP-Nonce': config.restNonce }
        }),
        fetch(`${config.restApiUrl}kj-cockpit/v1/jetengine-glossary/4`, {
          headers: { 'X-WP-Nonce': config.restNonce }
        }),
        fetch(`${config.restApiUrl}kj-cockpit/v1/jetengine-glossary/3`, {
          headers: { 'X-WP-Nonce': config.restNonce }
        }),
        fetch(`${config.restApiUrl}kj-cockpit/v1/jetengine-glossary/7`, {
          headers: { 'X-WP-Nonce': config.restNonce }
        })
      ])

      if (isDev) console.log('Glossary responses:', { 
        countryStatus: countryRes.status, 
        categoryStatus: categoryRes.status, 
        smPlatformsStatus: smPlatformsRes.status,
        campaignTypeStatus: campaignTypeRes.status 
      })

      const [countryData, categoryData, smPlatformsData, campaignTypeData] = await Promise.all([
        countryRes.json(),
        categoryRes.json(),
        smPlatformsRes.json(),
        campaignTypeRes.json()
      ])

      if (isDev) console.log('Glossary data:', { countryData, categoryData, smPlatformsData, campaignTypeData })

      const processGlossaryData = (data: any, name: string) => {
        if (isDev) console.log(`Processing ${name} glossary:`, data)
        if (!data.success) {
          if (isDev) console.error(`${name} glossary failed:`, data.message)
          return []
        }
        
        // Handle new API structure with items array
        if (data.items && Array.isArray(data.items)) {
          const entries = data.items.map((item: any) => ({
            value: item.value || item.item_value,
            label: item.label || item.item_label || item.value || item.item_value
          }))
          if (isDev) console.log(`${name} glossary processed entries from items:`, entries)
          return entries
        }
        
        // Handle old API structure with data object (fallback)
        if (data.data && typeof data.data === 'object') {
          const entries = Object.entries(data.data).map(([value, label]) => ({ value, label: String(label) }))
          if (isDev) console.log(`${name} glossary processed entries from data:`, entries)
          return entries
        }
        
        if (isDev) console.warn(`${name} glossary has no items or data`)
        return []
      }

      setGlossaryOptions({
        country: processGlossaryData(countryData, 'Country'),
        category: processGlossaryData(categoryData, 'Category'),
        social_media_platforms: processGlossaryData(smPlatformsData, 'SM Platforms'),
        campaign_type: processGlossaryData(campaignTypeData, 'Campaign Type')
      })
    } catch (error) {
      console.error('Error loading glossary options:', error)
    }
  }

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!id) {
        setError('Campaign ID not provided')
        setLoading(false)
        return
      }

      try {
        const config = (window as any).WP_COCKPIT
        if (!config?.restApiUrl || !config?.restNonce) {
          throw new Error('WordPress connection not available')
        }

        const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/campaign/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': config.restNonce,
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        
        if (data.success && data.data) {
          setCampaign(data.data)
        } else {
          throw new Error(data.message || 'Failed to fetch campaign details')
        }
      } catch (err) {
        console.error('Error fetching campaign details:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch campaign details')
      } finally {
        setLoading(false)
      }
    }

    fetchCampaignDetails()
  }, [id])

  // Initialize form data when campaign is loaded
  useEffect(() => {
    if (campaign && !isEditing) {
      setFormData({
        title: campaign.title.rendered,
        status: campaign.status,
        company_name: campaign.company.name,
        campaign_type: campaign.campaign_type.map(item => item.value),
        country: campaign.country.map(item => item.value),
        category: campaign.category.map(item => item.value),
        social_media_platforms: campaign.social_media_platforms.map(item => item.value),
        start_date: campaign.start_date,
        end_date: campaign.end_date,
        featured_image: campaign.featured_image,
        description: campaign.description
      })
    }
  }, [campaign, isEditing])

  // Load glossary options on component mount
  useEffect(() => {
    loadGlossaryOptions()
  }, [])

  // Handle form field changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear field error when user starts editing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Handle badge selection for glossary fields
  const handleBadgeToggle = (field: string, value: string) => {
    setFormData(prev => {
      const currentValues = prev[field as keyof typeof prev] as string[]
      
      // Single select for country and campaign_type
      if (field === 'country' || field === 'campaign_type') {
        const newValues = currentValues.includes(value) ? [] : [value]
        return {
          ...prev,
          [field]: newValues
        }
      }
      
      // Multi-select for categories and social_media_platforms
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value]
      
      return {
        ...prev,
        [field]: newValues
      }
    })
    
    // Clear field error when user interacts with field
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Image upload via REST API (same as influencer profile)
  const uploadImage = async (file: File): Promise<string> => {
    const config = (window as any).WP_COCKPIT
    if (!config?.restApiUrl || !config?.restNonce) {
      throw new Error('WordPress connection not available')
    }

    const formData = new FormData()
    formData.append('image', file)
    formData.append('image_type', 'featured')

    const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/upload-image`, {
      method: 'POST',
      headers: {
        'X-WP-Nonce': config.restNonce,
      },
      body: formData
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to upload image')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error(data.message || 'Failed to upload image')
    }

    return data.url
  }

  // Handle image upload
  const handleImageUpload = async (file: File) => {
    setUploading(true)
    try {
      const imageUrl = await uploadImage(file)
      handleInputChange('featured_image', imageUrl)
    } catch (error) {
      console.error('Error uploading image:', error)
      setError(error instanceof Error ? error.message : 'Failed to upload image')
    } finally {
      setUploading(false)
    }
  }

  // Validate required fields for publishing
  const validateForPublish = () => {
    const errors: Record<string, string> = {}
    
    if (!formData.title.trim()) {
      errors.title = 'Campaign title is required'
    }
    
    if (!formData.company_name.trim()) {
      errors.company_name = 'Company name is required'
    }
    
    if (formData.campaign_type.length === 0) {
      errors.campaign_type = 'Campaign type is required'
    }
    
    if (formData.country.length === 0) {
      errors.country = 'Country is required'
    }
    
    if (formData.category.length === 0) {
      errors.category = 'At least one category is required'
    }
    
    if (formData.social_media_platforms.length === 0) {
      errors.social_media_platforms = 'At least one social media platform is required'
    }
    
    if (!formData.start_date) {
      errors.start_date = 'Start date is required'
    }
    
    if (!formData.end_date) {
      errors.end_date = 'End date is required'
    }
    
    if (!formData.featured_image) {
      errors.featured_image = 'Featured image is required'
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Campaign description is required'
    }
    
    return errors
  }

  // Save campaign changes
  const handleSave = async () => {
    // Clear previous errors
    setFieldErrors({})
    setError(null)
    
    // Validate if trying to publish
    if (formData.status === 'publish') {
      const validationErrors = validateForPublish()
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors)
        return
      }
    }

    setSaving(true)
    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        throw new Error('WordPress connection not available')
      }

      const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/campaign/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.restNonce,
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.success && data.data) {
        setCampaign(data.data)
        setIsEditing(false)
      } else {
        throw new Error(data.message || 'Failed to save campaign')
      }
    } catch (err) {
      console.error('Error saving campaign:', err)
      setError(err instanceof Error ? err.message : 'Failed to save campaign')
    } finally {
      setSaving(false)
    }
  }

  // Delete campaign
  const handleDelete = async () => {
    setDeleting(true)
    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        throw new Error('WordPress connection not available')
      }

      const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/campaign/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.restNonce,
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      if (data.success) {
        // Navigate back to company profile
        navigate('/influencer-profile')
      } else {
        throw new Error(data.message || 'Failed to delete campaign')
      }
    } catch (err) {
      console.error('Error deleting campaign:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete campaign')
    } finally {
      setDeleting(false)
    }
  }

  const handleBack = () => {
    navigate('/influencer-profile')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Spinner className="h-8 w-8 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading campaign details...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Button onClick={handleBack} variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Profile
            </Button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Campaign Details</h1>
          </div>

          <Alert variant="destructive">
            <p>{error || 'Campaign not found'}</p>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button onClick={handleBack} variant="outline" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Profile
          </Button>
          <div className="flex items-start justify-between">
            <div>
              {isEditing ? (
                <div className="space-y-2">
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className={`text-3xl font-bold bg-transparent border-0 px-0 h-auto text-gray-900 dark:text-gray-100 ${fieldErrors.title ? 'border-red-500' : ''}`}
                    placeholder="Campaign title..."
                  />
                  {fieldErrors.title && (
                    <p className="text-red-500 text-sm">{fieldErrors.title}</p>
                  )}
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Campaign by {campaign.company.name}
                  </p>
                </div>
              ) : (
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {campaign.title.rendered}
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Campaign by {campaign.company.name}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="publish">Published</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleSave} disabled={saving || uploading}>
                    <Save className="h-4 w-4 mr-2" />
                    {uploading ? 'Image Uploading...' : saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button onClick={() => setIsEditing(false)} variant="outline">
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={deleting}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        {deleting ? 'Deleting...' : 'Delete Campaign'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{campaign.title.rendered}"? This action cannot be undone and will permanently remove the campaign and all its data.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                          Delete Campaign
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge 
                    variant={getStatusBadgeVariant(campaign.status)}
                    className="text-sm px-3 py-1"
                  >
                    {capitalizeFirst(campaign.status)}
                  </Badge>
                  {campaign.status === 'publish' && (
                    <Button 
                      onClick={() => window.open(campaign.permalink, '_blank')}
                      variant="outline"
                    >
                      View Campaign
                    </Button>
                  )}
                  <Button onClick={() => setIsEditing(true)} variant="outline">
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Campaign
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Campaign Details */}
        <div className="grid gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Campaign Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm text-gray-500">Campaign Type</Label>
                  {isEditing ? (
                    <div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {glossaryOptions.campaign_type.map((type) => (
                          <Badge
                            key={type.value}
                            variant={formData.campaign_type.includes(type.value) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => handleBadgeToggle('campaign_type', type.value)}
                          >
                            {type.label}
                          </Badge>
                        ))}
                      </div>
                      {fieldErrors.campaign_type && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.campaign_type}</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      {campaign.campaign_type.length > 0 && getCampaignTypeIcon(campaign.campaign_type[0].value)}
                      <p className="font-medium">{formatGlossaryLabels(campaign.campaign_type)}</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Country</Label>
                  {isEditing ? (
                    <div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {glossaryOptions.country.map((country) => (
                          <Badge
                            key={country.value}
                            variant={formData.country.includes(country.value) ? 'default' : 'outline'}
                            className="cursor-pointer flex items-center gap-1"
                            onClick={() => handleBadgeToggle('country', country.value)}
                          >
                            {getCountryFlag(country.value)}
                            {country.label}
                          </Badge>
                        ))}
                      </div>
                      {fieldErrors.country && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.country}</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      {campaign.country.length > 0 && getCountryFlag(campaign.country[0].value)}
                      <p className="font-medium">{formatGlossaryLabels(campaign.country)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div>
                <Label className="text-sm text-gray-500">Categories</Label>
                {isEditing ? (
                  <div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {glossaryOptions.category.map((category) => (
                        <Badge
                          key={category.value}
                          variant={formData.category.includes(category.value) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => handleBadgeToggle('category', category.value)}
                        >
                          {category.label}
                        </Badge>
                      ))}
                    </div>
                    {fieldErrors.category && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors.category}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {campaign.category.map((cat, index) => (
                      <Badge key={index} variant="secondary">
                        {cat.label}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Social Media Platforms */}
              <div>
                <Label className="text-sm text-gray-500">Social Media Platforms</Label>
                {isEditing ? (
                  <div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {glossaryOptions.social_media_platforms.map((platform) => (
                        <Badge
                          key={platform.value}
                          variant={formData.social_media_platforms.includes(platform.value) ? 'default' : 'outline'}
                          className="cursor-pointer flex items-center gap-1"
                          onClick={() => handleBadgeToggle('social_media_platforms', platform.value)}
                        >
                          <div className="w-4 h-4">
                            {getSocialMediaIcon(platform.value)}
                          </div>
                          {platform.label}
                        </Badge>
                      ))}
                    </div>
                    {fieldErrors.social_media_platforms && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors.social_media_platforms}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {campaign.social_media_platforms.map((platform, index) => (
                      <Badge key={index} variant="outline" className="flex items-center gap-1">
                        <div className="w-4 h-4">
                          {getSocialMediaIcon(platform.value)}
                        </div>
                        {platform.label}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label className="text-sm text-gray-500">Start Date</Label>
                  {isEditing ? (
                    <div>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        className={`mt-1 ${fieldErrors.start_date ? 'border-red-500' : ''}`}
                      />
                      {fieldErrors.start_date && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.start_date}</p>
                      )}
                    </div>
                  ) : (
                    <p className="font-medium mt-1">{formatDate(campaign.start_date)}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-gray-500">End Date</Label>
                  {isEditing ? (
                    <div>
                      <Input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                        className={`mt-1 ${fieldErrors.end_date ? 'border-red-500' : ''}`}
                      />
                      {fieldErrors.end_date && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.end_date}</p>
                      )}
                    </div>
                  ) : (
                    <p className="font-medium mt-1">{formatDate(campaign.end_date)}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Created</Label>
                  <p className="font-medium mt-1">{formatDate(campaign.created_date)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label className="text-sm text-gray-500">Company</Label>
                {isEditing ? (
                  <div>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      className={`mt-1 ${fieldErrors.company_name ? 'border-red-500' : ''}`}
                      placeholder="Company name..."
                    />
                    {fieldErrors.company_name && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors.company_name}</p>
                    )}
                  </div>
                ) : (
                  <p className="font-medium mt-1">{campaign.company.name}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Featured Image */}
          <Card>
            <CardHeader>
              <CardTitle>Featured Image</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-4">
                  {formData.featured_image && (
                    <div className="rounded-lg overflow-hidden">
                      <img 
                        src={formData.featured_image} 
                        alt="Featured image preview"
                        className="w-full h-auto max-h-96 object-cover"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <Button
                      onClick={() => {
                        const input = document.createElement('input')
                        input.type = 'file'
                        input.accept = 'image/*'
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0]
                          if (file) {
                            handleImageUpload(file)
                          }
                        }
                        input.click()
                      }}
                      disabled={uploading}
                      variant="outline"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                    {formData.featured_image && (
                      <Button
                        onClick={() => handleInputChange('featured_image', '')}
                        variant="outline"
                        size="sm"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                  {fieldErrors.featured_image && (
                    <p className="text-red-500 text-sm">{fieldErrors.featured_image}</p>
                  )}
                </div>
              ) : (
                campaign.featured_image ? (
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={campaign.featured_image} 
                      alt={campaign.title.rendered}
                      className="w-full h-auto max-h-96 object-cover"
                    />
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No featured image set
                  </div>
                )
              )}
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter campaign description..."
                    className={`min-h-32 ${fieldErrors.description ? 'border-red-500' : ''}`}
                  />
                  {fieldErrors.description && (
                    <p className="text-red-500 text-sm mt-1">{fieldErrors.description}</p>
                  )}
                </div>
              ) : (
                campaign.description ? (
                  <div className="prose max-w-none">
                    <p className="whitespace-pre-wrap">{campaign.description}</p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No description provided
                  </div>
                )
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}